(() => {
  'use strict';

  const PACK_TYPES = new Set(['bread', 'cookie', 'merch']);

  const RATES = Object.freeze({
    merch_only: 1000,
    small: 1499,
    medium: 1999,
    large: 2999,
  });

  function number(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  function normalizePackType(value) {
    const type = String(value || '').trim().toLowerCase();
    return PACK_TYPES.has(type) ? type : '';
  }

  function createCounts({
    bread = 0,
    cookie = 0,
    merch = 0,
  } = {}) {
    const counts = {
      bread: number(bread),
      cookie: number(cookie),
      merch: number(merch),
    };

    counts.loafEquivalent = counts.bread + counts.merch;
    counts.totalItems =
      counts.bread +
      counts.cookie +
      counts.merch;

    return counts;
  }

  function invalid(counts, reason, message) {
    return {
      valid: false,
      tier: 'invalid',
      box: null,
      rateCents: null,
      reason,
      message,
      counts,
    };
  }

  function valid(counts, tier, box, rateCents) {
    return {
      valid: true,
      tier,
      box,
      rateCents,
      reason: '',
      message: '',
      counts,
    };
  }

  function classifyCounts(input = {}) {
    const counts = createCounts(input);
    const {
      bread,
      cookie,
      merch,
      loafEquivalent,
      totalItems,
    } = counts;

    if (totalItems === 0) {
      return valid(counts, 'empty', null, 0);
    }

    if (cookie > 10) {
      return invalid(
        counts,
        'too_many_cookies',
        'Nationwide shipping is limited to 10 cookies per order.'
      );
    }

    if (loafEquivalent > 5) {
      return invalid(
        counts,
        'too_many_loaf_equivalents',
        'This order exceeds the capacity of our largest shipping box.'
      );
    }

    if (cookie > 0 && loafEquivalent >= 5) {
      return invalid(
        counts,
        'five_loaf_equivalents_with_cookies',
        'Five loaves or loaf-sized items fill the Large Box, so cookies cannot be added.'
      );
    }

    /*
      Merch-only orders:
      All nationwide merch counts as one loaf-equivalent for capacity,
      but a cart containing only merch receives the $10 flat merch rate.
    */
    if (
      merch > 0 &&
      bread === 0 &&
      cookie === 0
    ) {
      return valid(
        counts,
        'merch_only',
        'Merch',
        RATES.merch_only
      );
    }

    /*
      COOKIE ONLY
      1–5 cookies  -> Small
      6–10 cookies -> Medium
    */
    if (cookie > 0 && loafEquivalent === 0) {
      if (cookie <= 5) {
        return valid(
          counts,
          'small',
          'Small',
          RATES.small
        );
      }

      return valid(
        counts,
        'medium',
        'Medium',
        RATES.medium
      );
    }

    /*
      LOAF / LOAF-EQUIVALENT ONLY
      1–2 -> Medium
      3–5 -> Large

      Merch counts as one loaf-equivalent whenever baked goods
      are also present.
    */
    if (cookie === 0 && loafEquivalent > 0) {
      if (loafEquivalent <= 2) {
        return valid(
          counts,
          'medium',
          'Medium',
          RATES.medium
        );
      }

      return valid(
        counts,
        'large',
        'Large',
        RATES.large
      );
    }

    /*
      MIXED
      1 loaf-equivalent + 1–5 cookies -> Medium

      Anything beyond that which remains within:
      max 4 loaf-equivalents + max 10 cookies -> Large
    */
    if (cookie > 0 && loafEquivalent > 0) {
      if (
        loafEquivalent === 1 &&
        cookie <= 5
      ) {
        return valid(
          counts,
          'medium',
          'Medium',
          RATES.medium
        );
      }

      if (
        loafEquivalent <= 4 &&
        cookie <= 10
      ) {
        return valid(
          counts,
          'large',
          'Large',
          RATES.large
        );
      }
    }

    return invalid(
      counts,
      'invalid_combination',
      'That combination will not fit in our available shipping boxes.'
    );
  }

  function getItemPackType(item) {
    const properties = item?.properties || {};

    return normalizePackType(
      properties._cold_sugar_shipping_pack_type ||
      item?.shippingPackType ||
      item?.packType
    );
  }

  function isShippingItem(item) {
    const properties = item?.properties || {};

    const mode = String(
      properties._cold_sugar_order_mode ||
      item?.orderMode ||
      ''
    )
      .trim()
      .toLowerCase();

    return mode === 'shipping';
  }

  function isSystemItem(item) {
    const properties = item?.properties || {};

    return (
      properties._cold_sugar_pickup_lock === 'true' ||
      properties._cold_sugar_shipping_box_lock === 'true'
    );
  }

  function summarizeItems(
    items = [],
    {
      overrideLineKey = '',
      overrideQuantity = null,
    } = {}
  ) {
    const raw = {
      bread: 0,
      cookie: 0,
      merch: 0,
    };

    let unclassifiedShippingQuantity = 0;

    (items || []).forEach((item) => {
      if (!item || isSystemItem(item)) return;
      if (!isShippingItem(item)) return;

      let quantity = number(item.quantity);

      if (
        overrideLineKey &&
        String(item.key || '') === String(overrideLineKey)
      ) {
        quantity = number(overrideQuantity);
      }

      if (quantity === 0) return;

      const packType = getItemPackType(item);

      if (!packType) {
        unclassifiedShippingQuantity += quantity;
        return;
      }

      raw[packType] += quantity;
    });

    const result = classifyCounts(raw);

    if (unclassifiedShippingQuantity > 0) {
      return {
        ...invalid(
          result.counts,
          'unclassified_shipping_item',
          'One or more shipped items are missing box-packing information.'
        ),
        unclassifiedShippingQuantity,
      };
    }

    return {
      ...result,
      unclassifiedShippingQuantity: 0,
    };
  }

  function previewAdd(items, packType, quantity = 1) {
    const current = summarizeItems(items);

    if (
      !current.valid &&
      current.reason === 'unclassified_shipping_item'
    ) {
      return current;
    }

    const type = normalizePackType(packType);

    if (!type) {
      return invalid(
        current.counts,
        'invalid_pack_type',
        'This item is not configured for nationwide box packing.'
      );
    }

    const next = {
      bread: current.counts.bread,
      cookie: current.counts.cookie,
      merch: current.counts.merch,
    };

    next[type] += number(quantity);

    return classifyCounts(next);
  }

  function previewLineQuantity(
    items,
    lineKey,
    quantity
  ) {
    return summarizeItems(items, {
      overrideLineKey: lineKey,
      overrideQuantity: quantity,
    });
  }

  function maxAdditionalForSameTier(
    input,
    packType
  ) {
    const current = classifyCounts(input);
    const type = normalizePackType(packType);

    if (!current.valid || !type || current.tier === 'empty') {
      return 0;
    }

    let max = 0;

    for (let add = 1; add <= 20; add += 1) {
      const next = {
        bread: current.counts.bread,
        cookie: current.counts.cookie,
        merch: current.counts.merch,
      };

      next[type] += add;

      const result = classifyCounts(next);

      if (
        !result.valid ||
        result.tier !== current.tier
      ) {
        break;
      }

      max = add;
    }

    return max;
  }

  function maxAdditionalOverall(
    input,
    packType
  ) {
    const current = classifyCounts(input);
    const type = normalizePackType(packType);

    if (!current.valid || !type) return 0;

    let max = 0;

    for (let add = 1; add <= 20; add += 1) {
      const next = {
        bread: current.counts.bread,
        cookie: current.counts.cookie,
        merch: current.counts.merch,
      };

      next[type] += add;

      if (!classifyCounts(next).valid) break;

      max = add;
    }

    return max;
  }

  function formatContents(input = {}) {
    const counts = createCounts(input);
    const parts = [];

    if (counts.bread > 0) {
      parts.push(
        `${counts.bread} ${counts.bread === 1 ? 'loaf' : 'loaves'}`
      );
    }

    if (counts.cookie > 0) {
      parts.push(
        `${counts.cookie} ${counts.cookie === 1 ? 'cookie' : 'cookies'}`
      );
    }

    if (counts.merch > 0) {
      parts.push(
        `${counts.merch} merch ${counts.merch === 1 ? 'item' : 'items'}`
      );
    }

    return parts.join(' + ');
  }

  function joinWithOr(parts = []) {
    if (parts.length <= 1) {
      return parts[0] || '';
    }

    if (parts.length === 2) {
      return `${parts[0]} or ${parts[1]}`;
    }

    return `${parts.slice(0, -1).join(', ')}, or ${parts.at(-1)}`;
  }

  function describeCapacity(result) {
    if (!result?.valid) {
      return result?.message || '';
    }

    if (result.tier === 'empty') {
      return '';
    }

    /*
      Merch-only carts stay at the flat $10 merch rate until
      baked goods are introduced.
    */
    if (result.tier === 'merch_only') {
      const merchRoom =
        maxAdditionalForSameTier(
          result.counts,
          'merch'
        );

      if (merchRoom <= 0) {
        return 'Your merch-only order has reached its packing limit.';
      }

      const roomCopy =
        merchRoom === 1
          ? 'You can still add 1 more merch item at the $10 flat shipping rate.'
          : `You can still add up to ${merchRoom} more merch items at the $10 flat shipping rate.`;

      return `${roomCopy} Adding baked goods will switch your order to a bakery-box shipping rate.`;
    }

    const sameCookieRoom =
      maxAdditionalForSameTier(
        result.counts,
        'cookie'
      );

    const sameBreadRoom =
      maxAdditionalForSameTier(
        result.counts,
        'bread'
      );

    const sameMerchRoom =
      maxAdditionalForSameTier(
        result.counts,
        'merch'
      );

    const sameLoafOrMerchRoom =
      Math.max(
        sameBreadRoom,
        sameMerchRoom
      );

    const overallCookieRoom =
      maxAdditionalOverall(
        result.counts,
        'cookie'
      );

    const overallBreadRoom =
      maxAdditionalOverall(
        result.counts,
        'bread'
      );

    const overallMerchRoom =
      maxAdditionalOverall(
        result.counts,
        'merch'
      );

    const overallLoafOrMerchRoom =
      Math.max(
        overallBreadRoom,
        overallMerchRoom
      );

    const sentences = [];

    const loafRoomCopy =
      sameLoafOrMerchRoom === 1
        ? '1 more loaf-sized item (a loaf or merch item)'
        : sameLoafOrMerchRoom > 1
          ? `up to ${sameLoafOrMerchRoom} more loaf-sized items (loaves or merch)`
          : '';

    const cookieRoomCopy =
      sameCookieRoom === 1
        ? '1 more cookie'
        : sameCookieRoom > 1
          ? `up to ${sameCookieRoom} more cookies`
          : '';

    if (loafRoomCopy && cookieRoomCopy) {
      sentences.push(
        `You still have room at this shipping rate. Depending on what you add next, you can fit ${loafRoomCopy}, or ${cookieRoomCopy}.`
      );
    } else if (loafRoomCopy) {
      sentences.push(
        `You can still add ${loafRoomCopy} at this shipping rate.`
      );
    } else if (cookieRoomCopy) {
      sentences.push(
        `You can still add ${cookieRoomCopy} at this shipping rate.`
      );
    }

    /*
      Once the cart reaches 10 cookies, additional cookies are
      never allowed — even if another product type could move
      the order into a larger box.
    */
    if (
      result.counts.cookie >= 10 &&
      result.tier !== 'large'
    ) {
      sentences.push(
        'You’ve reached the 10-cookie limit.'
      );
    }

    if (
      result.tier === 'small' ||
      result.tier === 'medium'
    ) {
      const cookieMovesUp =
        sameCookieRoom === 0 &&
        overallCookieRoom > 0;

      const loafOrMerchMovesUp =
        sameLoafOrMerchRoom === 0 &&
        overallLoafOrMerchRoom > 0;

      if (
        cookieMovesUp ||
        loafOrMerchMovesUp
      ) {
        const nextBox =
          result.tier === 'small'
            ? 'Medium'
            : 'Large';

        if (
          cookieMovesUp &&
          loafOrMerchMovesUp
        ) {
          sentences.push(
            `Adding another cookie, a loaf, or a merch item would move your order to a ${nextBox} Box.`
          );
        } else if (cookieMovesUp) {
          sentences.push(
            `Adding another cookie would move your order to a ${nextBox} Box.`
          );
        } else {
          sentences.push(
            `Adding a loaf or merch item would move your order to a ${nextBox} Box.`
          );
        }
      }
    }

    if (
      result.tier === 'large' &&
      sameCookieRoom === 0 &&
      sameLoafOrMerchRoom === 0
    ) {
      sentences.push(
        'Your Large Shipping Box is full.'
      );
    }

    return sentences.join(' ');
  }

  const api = Object.freeze({
    RATES,
    normalizePackType,
    createCounts,
    classifyCounts,
    summarizeItems,
    previewAdd,
    previewLineQuantity,
    maxAdditionalForSameTier,
    maxAdditionalOverall,
    formatContents,
    describeCapacity,
  });

  if (typeof window !== 'undefined') {
    window.ColdSugarShippingBox = api;
  } else {
    globalThis.ColdSugarShippingBox = api;
  }
})();
